import axios from 'api';
import { store } from 'store/index';

// ───────────────────────────────────────────────────────────────────────────
// Public, read-only Blog API (see BLOG_API_FRONTEND_SPEC.md).
//
//   Base URL : /api/blog/
//   Auth     : none (AllowAny) — the shared axios instance still sends
//              cookies, which is harmless here.
//   Visibility: only *live* posts (status=published & published_at<=now) are
//              ever returned; drafts / scheduled posts respond 404.
//   Rate limit: 120 req/min per IP (`blog_read`) — a 429 means back off.
// ───────────────────────────────────────────────────────────────────────────

const BASE = '/api/blog';

// Superuser bearer token from the auth store (this panel is superuser-only).
// Reads are public, but sending the token lets a super-admin see every status
// (draft / scheduled / archived), not just the public `published` posts.
const bearerHeaders = () => {
  const access = store.getState()?.auth?.tokens?.access;
  return access ? { Authorization: `Bearer ${access}` } : {};
};

// GET /api/blog/posts/ — paginated, filterable list (list shape, no `body`).
//
// Builds exactly the query shape the spec documents, e.g.:
//   /api/blog/posts/?page=2&page_size=9
//   /api/blog/posts/?categories__slug=product-updates
//   /api/blog/posts/?tags__slug=ai&ordering=-view_count
//   /api/blog/posts/?search=whatsapp&status=draft
//
// Callers pass friendly keys (`category`, `tag`); we translate them to the
// backend filter keys (`categories__slug`, `tags__slug`). Empty/undefined
// values are omitted so we never send blank params. `status` is only honoured
// for an authenticated super-admin.
export const getBlogPosts = (filtersData, signal) => {
  const { page, page_size, category, tag, search, ordering, status } = filtersData || {};
  const params = new URLSearchParams();

  if (page != null) params.set('page', page);
  // Honour the documented ceiling (max 50 per page).
  if (page_size != null) params.set('page_size', Math.min(Number(page_size) || 12, 50));
  if (category) params.set('categories__slug', category);
  if (tag) params.set('tags__slug', tag);
  if (search) params.set('search', search);
  if (status) params.set('status', status);
  // `ordering` is only sent for the allowed values (`published_at`,
  // `view_count`, prefixed with `-` for desc); the default server ordering is
  // omitted by the caller so we don't send an unsupported value.
  if (ordering) params.set('ordering', ordering);

  const qs = params.toString();
  return axios.get(qs ? `${BASE}/posts/?${qs}` : `${BASE}/posts/`, { signal, headers: bearerHeaders() });
};

// GET /api/blog/posts/{slug}/ — full post (body + seo). Increments view_count.
// Sent authenticated so a super-admin can open non-published drafts too.
export const getBlogPost = (slug, signal) => {
  return axios.get(`${BASE}/posts/${slug}/`, { signal, headers: bearerHeaders() });
};

// GET /api/blog/categories/ — un-paginated array. `parent` builds the nav tree.
export const getBlogCategories = (signal) => {
  return axios.get(`${BASE}/categories/`, { signal });
};

// GET /api/blog/tags/ — un-paginated array.
export const getBlogTags = (signal) => {
  return axios.get(`${BASE}/tags/`, { signal });
};

// ───────────────────────────────────────────────────────────────────────────
// Authoring (write) — super-admin only. The spec gates POST/PATCH/PUT/DELETE
// behind an active-superuser JWT: `Authorization: Bearer <access>` (no CSRF
// needed for bearer requests). We read that token from the auth store and
// attach it per write request. Resources are addressed by slug; slugs are
// auto-generated server-side (never sent).
//
// Posts reference images by media-library id (`featured_media` / `og_media`) —
// there is no media-upload API; images are uploaded in the Django admin.
// Categories/tags are sent as arrays of ids.
// ───────────────────────────────────────────────────────────────────────────

// Bearer-authed config for a write request.
const authConfig = () => ({ headers: bearerHeaders() });

// Posts
export const createBlogPost = (payload) => axios.post(`${BASE}/posts/`, payload, authConfig());
export const updateBlogPost = (slug, payload) => axios.patch(`${BASE}/posts/${slug}/`, payload, authConfig());
export const deleteBlogPost = (slug) => axios.delete(`${BASE}/posts/${slug}/`, authConfig());

// Lifecycle actions — each returns the updated post (detail shape) and writes a
// revision snapshot. publish auto-stamps published_at on first publish;
// unpublish reverts to draft; archive retires without deleting.
export const publishBlogPost = (slug) => axios.post(`${BASE}/posts/${slug}/publish/`, null, authConfig());
export const unpublishBlogPost = (slug) => axios.post(`${BASE}/posts/${slug}/unpublish/`, null, authConfig());
export const archiveBlogPost = (slug) => axios.post(`${BASE}/posts/${slug}/archive/`, null, authConfig());

// Categories (name, description, parent id | null)
export const createBlogCategory = (payload) => axios.post(`${BASE}/categories/`, payload, authConfig());
export const updateBlogCategory = (slug, payload) => axios.patch(`${BASE}/categories/${slug}/`, payload, authConfig());
export const deleteBlogCategory = (slug) => axios.delete(`${BASE}/categories/${slug}/`, authConfig());

// Tags (name)
export const createBlogTag = (payload) => axios.post(`${BASE}/tags/`, payload, authConfig());
export const updateBlogTag = (slug, payload) => axios.patch(`${BASE}/tags/${slug}/`, payload, authConfig());
export const deleteBlogTag = (slug) => axios.delete(`${BASE}/tags/${slug}/`, authConfig());

// Media library — upload an asset (multipart) and get back a media object
// { id, url, media_type, width, height, ... }. Reference its `id` on a post as
// `featured_media` / `og_media`. `media_type`/dimensions are derived server-side.
export const uploadBlogMedia = (file, meta = {}) => {
  const fd = new FormData();
  fd.append('file', file);
  if (meta.title) fd.append('title', meta.title);
  if (meta.alt_text) fd.append('alt_text', meta.alt_text);
  if (meta.caption) fd.append('caption', meta.caption);
  // Clear the instance's default JSON Content-Type so the browser sets the
  // multipart boundary; attach the superuser bearer token.
  return axios.post(`${BASE}/media/`, fd, { headers: { 'Content-Type': undefined, ...bearerHeaders() } });
};
